import { useState } from "react";
import { Mail, Clock, MessageSquare, AlertCircle, CheckCircle } from "lucide-react";
import emailjs from "@emailjs/browser";

const CONTACT_TOPICS = [
  "Order status / tracking",
  "Wrong item received",
  "Damaged product on delivery",
  "Custom name printing issue",
  "Payment failure or double charge",
  "Other",
];


const ContactPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false); 
  const [form, setForm] = useState({ name: "", email: "", orderId: "", topic: "", message: "" });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitting(true);

  try {
    await emailjs.send(
      "service_3wikyhz",      // serviceId from EmailJS dashboard
      "template_7k2dlnr",     // templateId from EmailJS dashboard
      {
        from_name: form.name,
        from_email: form.email,
        order_id: form.orderId || "N/A",
        topic: form.topic,
        message: form.message,
      },
      "YrzYL5bXK0inB3WmQ"       // public key from EmailJS dashboard → Account
    );
    setSubmitted(true);
  } catch (err) {
    alert("Failed to send. Please email us directly at support@collegedrip.in");
  } finally {
    setSubmitting(false);
  }
};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Strip */}
      <div className="bg-blue-700 text-white py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-blue-200 text-sm font-medium uppercase tracking-widest mb-2">Support</p>
          <h1 className="text-4xl font-extrabold mb-3">Contact Us</h1>
          <p className="text-blue-100 text-lg max-w-xl">
            We're a small team that genuinely cares. If something's off, we'll make it right.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12 grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Left — Info Cards */}
        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mb-3">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Email Us</h3>
            <p className="text-sm text-gray-500 mb-2">Best way to reach us</p>
            <a
              href="mailto:support@collegedrip.in"
              className="text-blue-600 font-medium text-sm hover:underline break-all"
            >
              support@collegedrip.in
            </a>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mb-3">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Response Time</h3>
            <p className="text-sm text-gray-500">
              We reply within <span className="font-medium text-gray-800">24–48 hours</span> on weekdays.
              Weekend queries are handled on Monday.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="flex gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <h3 className="font-semibold text-amber-800 text-sm">Before writing in</h3>
            </div>
            <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
              <li>Check <strong>My Orders</strong> for live status</li>
              <li>Most delivery queries resolve within the 5-day window</li>
              <li>Check spam for confirmation emails</li>
            </ul>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mb-3">
              <MessageSquare className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">We can help with</h3>
            <ul className="text-sm text-gray-500 space-y-1.5">
              {CONTACT_TOPICS.slice(0, 5).map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right — Contact Form */}
        <div className="md:col-span-2">
          {submitted ? (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-10 flex flex-col items-center text-center h-full justify-center">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Message sent!</h2>
              <button
                onClick={() => setSubmitted(false)}
                className="mt-6 text-sm text-blue-600 hover:underline"
              >
                Send another message
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-7">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Send us a message</h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Your Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Rohan Sharma"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Order ID <span className="text-gray-400 font-normal">(if applicable)</span>
                  </label>
                  <input
                    type="text"
                    name="orderId"
                    value={form.orderId}
                    onChange={handleChange}
                    placeholder="CD-XXXXXXXX"
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Topic <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="topic"
                    required
                    value={form.topic}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                  >
                    <option value="">Select a topic</option>
                    {CONTACT_TOPICS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Describe your issue in detail. For damaged/wrong items, describe what you received."
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
                  />
                </div>

            <button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-3 rounded-lg transition text-sm"
            >
            {submitting ? "Sending..." : "Send Message"}
            </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactPage;